'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Home, Building2, Heart, ArrowRight, Bed, Bath, Square, ChevronRight, Shield, TrendingUp } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import HomeButton from '@/components/HomeButton';

interface PropertyListing {
  id: string;
  title: string;
  type: string;
  listing_type: 'sale' | 'rent';
  price: number;
  location: string;
  city: string;
  bedrooms: number;
  bathrooms: number;
  area_sqft: number;
  image_url: string;
  agent: string;
  is_featured: boolean;
  is_verified: boolean;
  amenities: string[];
  description: string;
}

const PROPERTIES: PropertyListing[] = [
{ id: '1', title: '3-Bedroom Apartment — Westlands', type: 'Apartment', listing_type: 'rent', price: 85000, location: 'Westlands', city: 'Nairobi', bedrooms: 3, bathrooms: 2, area_sqft: 1400, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee3d460c-1784530799137.png", agent: 'Peter M', is_featured: true, is_verified: true, amenities: ['Parking', 'Security', 'Gym', 'Pool'], description: 'Modern apartment in prime Westlands location. Fully furnished option available.' },
{ id: '2', title: '4-Bedroom Townhouse — Karen', type: 'Townhouse', listing_type: 'sale', price: 18500000, location: 'Karen', city: 'Nairobi', bedrooms: 4, bathrooms: 3, area_sqft: 2800, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15a66cff5-1769361478601.png", agent: 'Faith N', is_featured: true, is_verified: true, amenities: ['Garden', 'Parking', 'Security', 'Borehole'], description: 'Spacious family home in serene Karen. Large garden and servant quarters.' },
{ id: '3', title: 'Bedsitter — Kilimani', type: 'Bedsitter', listing_type: 'rent', price: 18000, location: 'Kilimani', city: 'Nairobi', bedrooms: 0, bathrooms: 1, area_sqft: 350, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_12cbdce40-1780725269581.png", agent: 'Samuel K', is_featured: false, is_verified: true, amenities: ['WiFi', 'Security'], description: 'Compact and affordable bedsitter in Kilimani. Walking distance to amenities.' },
{ id: '4', title: '2-Bedroom Apartment — Mombasa Road', type: 'Apartment', listing_type: 'rent', price: 45000, location: 'Mombasa Road', city: 'Nairobi', bedrooms: 2, bathrooms: 2, area_sqft: 900, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1e5efdc57-1784530797976.png", agent: 'Mary W', is_featured: false, is_verified: false, amenities: ['Parking', 'Security'], description: 'Well-maintained apartment near SGR station. Easy access to CBD.' },
{ id: '5', title: '50×100 Plot — Ruiru', type: 'Land', listing_type: 'sale', price: 3500000, location: 'Ruiru', city: 'Nairobi', bedrooms: 0, bathrooms: 0, area_sqft: 5000, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_19a9ac9ca-1767170621912.png", agent: 'David O', is_featured: false, is_verified: true, amenities: ['Title Deed', 'Road Access'], description: 'Prime residential plot with ready title deed. All utilities available.' },
{ id: '6', title: '3-Bedroom House — Kisumu', type: 'House', listing_type: 'rent', price: 35000, location: 'Milimani', city: 'Kisumu', bedrooms: 3, bathrooms: 2, area_sqft: 1200, image_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b3d90080-1784530797863.png", agent: 'Grace O', is_featured: false, is_verified: true, amenities: ['Garden', 'Parking', 'Security'], description: 'Comfortable family home in quiet Milimani estate. Lake views from upper floor.' }];


const PROPERTY_TYPES = ['All Types', 'Apartment', 'House', 'Townhouse', 'Bedsitter', 'Land', 'Commercial'];
const LISTING_TYPES = ['All', 'For Sale', 'For Rent'];
const CITIES = ['All Cities', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'];
const BEDROOMS = ['Any', '0 (Bedsitter)', '1+', '2+', '3+', '4+'];

export default function PropertyLandingPage() {
  const [search, setSearch] = useState('');
  const [propertyType, setPropertyType] = useState('All Types');
  const [listingType, setListingType] = useState('All');
  const [city, setCity] = useState('All Cities');
  const [bedrooms, setBedrooms] = useState('Any');
  const [maxPrice, setMaxPrice] = useState('');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const filtered = PROPERTIES.filter((p) => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.location.toLowerCase().includes(search.toLowerCase());
    const matchType = propertyType === 'All Types' || p.type === propertyType;
    const matchListing = listingType === 'All' || listingType === 'For Sale' && p.listing_type === 'sale' || listingType === 'For Rent' && p.listing_type === 'rent';
    const matchCity = city === 'All Cities' || p.city === city;
    const matchBeds = bedrooms === 'Any' || bedrooms === '0 (Bedsitter)' && p.bedrooms === 0 || bedrooms.includes('+') && p.bedrooms >= Number(bedrooms[0]);
    const matchPrice = !maxPrice || p.price <= Number(maxPrice);
    return matchSearch && matchType && matchListing && matchCity && matchBeds && matchPrice;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  }, []);

  const formatPrice = (p: PropertyListing) => {
    if (p.listing_type === 'rent') return `KES ${p.price.toLocaleString()}/mo`;
    return `KES ${(p.price / 1000000).toFixed(1)}M`;
  };

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a001a 0%, #0d0a2e 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #E040FB 0%, transparent 50%), radial-gradient(circle at 70% 30%, #7C4DFF 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-between mb-4">
              <HomeButton />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600"
              style={{ background: 'rgba(224,64,251,0.15)', color: '#E040FB', border: '1px solid rgba(224,64,251,0.3)' }}>
                <TrendingUp size={12} /> {6780 .toLocaleString()} Properties Listed
              </div>
              <Link href="/create-listing?type=property" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-700"
                style={{ background: '#E040FB', color: '#fff' }}>
                + List Property
              </Link>
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Find Your <span style={{ color: '#E040FB' }}>Dream Home</span> in Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Houses, apartments, land, and commercial properties for sale and rent across Kenya.
            </p>
            <div className="flex gap-2 max-w-lg mx-auto">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Location, area, or property type…"
                className="w-full pl-9 pr-3 py-3.5 rounded-2xl text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
              </div>
              <select value={city} onChange={(e) => setCity(e.target.value)}
              className="px-3 py-3.5 rounded-2xl text-sm outline-none appearance-none"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                {CITIES.map((c) => <option key={c} value={c} style={{ background: '#0D1526' }}>{c}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 sticky top-0 z-20"
        style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {LISTING_TYPES.map((t) =>
            <button key={t} onClick={() => setListingType(t)}
            className="px-3 py-2 rounded-xl text-xs font-700 flex-shrink-0 transition-all"
            style={{ background: listingType === t ? 'rgba(224,64,251,0.15)' : 'rgba(255,255,255,0.04)', color: listingType === t ? '#E040FB' : 'rgba(255,255,255,0.55)', border: `1px solid ${listingType === t ? 'rgba(224,64,251,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {t}
              </button>
            )}
            <div className="flex-shrink-0 w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
            {PROPERTY_TYPES.map((t) =>
            <button key={t} onClick={() => setPropertyType(t)}
            className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: propertyType === t ? 'rgba(224,64,251,0.15)' : 'rgba(255,255,255,0.04)', color: propertyType === t ? '#E040FB' : 'rgba(255,255,255,0.55)', border: `1px solid ${propertyType === t ? 'rgba(224,64,251,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {t}
              </button>
            )}
          </div>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pt-2">
            {BEDROOMS.map((b) =>
            <button key={b} onClick={() => setBedrooms(b)}
            className="px-3 py-1.5 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
            style={{ background: bedrooms === b ? 'rgba(224,64,251,0.15)' : 'rgba(255,255,255,0.04)', color: bedrooms === b ? '#E040FB' : 'rgba(255,255,255,0.5)', border: `1px solid ${bedrooms === b ? 'rgba(224,64,251,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                {b === 'Any' ? 'Any Beds' : b === '0 (Bedsitter)' ? 'Bedsitter' : `${b} Beds`}
              </button>
            )}
            <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max KES"
            className="w-28 px-3 py-1.5 rounded-xl text-xs outline-none flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#E040FB', fontWeight: 700 }}>{filtered.length}</span> properties found
            </span>
            <Link href="/login"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
            style={{ background: '#E040FB', color: '#fff' }}>
              List Property <ArrowRight size={12} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((property) =>
            <div key={property.id} className="rounded-2xl overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${property.is_featured ? 'rgba(224,64,251,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="relative h-48">
                  <img src={property.image_url} alt={property.title}
                className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)' }} />
                  {property.is_featured &&
                <span className="absolute top-2 left-2 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: '#E040FB', color: '#fff' }}>Featured</span>
                }
                  <span className="absolute top-2 right-10 text-[9px] px-1.5 py-0.5 rounded-md font-700"
                style={{ background: property.listing_type === 'sale' ? 'rgba(0,200,83,0.9)' : 'rgba(0,188,212,0.9)', color: '#fff' }}>
                    {property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
                  </span>
                  <button onClick={() => toggleSave(property.id)}
                className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center"
                style={{ background: savedIds.has(property.id) ? '#FF4081' : 'rgba(0,0,0,0.5)' }}>
                    <Heart size={12} fill={savedIds.has(property.id) ? '#fff' : 'none'} style={{ color: '#fff' }} />
                  </button>
                  <div className="absolute bottom-2 left-3">
                    <p className="text-base font-800 text-white">{formatPrice(property)}</p>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-700 text-white line-clamp-1">{property.title}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <MapPin size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{property.location}, {property.city}</span>
                      </div>
                    </div>
                    {property.is_verified && <Shield size={14} style={{ color: '#00C853', flexShrink: 0 }} />}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    {property.bedrooms > 0 &&
                  <div className="flex items-center gap-1">
                        <Bed size={11} style={{ color: 'rgba(255,255,255,0.4)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{property.bedrooms} Bed</span>
                      </div>
                  }
                    {property.bathrooms > 0 &&
                  <div className="flex items-center gap-1">
                        <Bath size={11} style={{ color: 'rgba(255,255,255,0.4)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{property.bathrooms} Bath</span>
                      </div>
                  }
                    <div className="flex items-center gap-1">
                      <Square size={11} style={{ color: 'rgba(255,255,255,0.4)' }} />
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{property.area_sqft.toLocaleString()} sqft</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {property.amenities.slice(0, 3).map((a) =>
                  <span key={a} className="text-[10px] px-1.5 py-0.5 rounded-md"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)' }}>{a}</span>
                  )}
                  </div>
                  <Link href={`/listing/property/${property.id}`}
                className="flex items-center justify-center gap-1.5 mt-3 py-2 rounded-xl text-xs font-700"
                style={{ background: 'rgba(224,64,251,0.12)', color: '#E040FB', border: '1px solid rgba(224,64,251,0.2)' }}>
                    View Property <ChevronRight size={11} />
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-8 p-6 rounded-3xl text-center"
          style={{ background: 'linear-gradient(135deg, rgba(224,64,251,0.1), rgba(124,77,255,0.1))', border: '1px solid rgba(224,64,251,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {6780 .toLocaleString()} Properties</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up to contact agents, save properties, and get alerts for new listings.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: '#E040FB', color: '#fff' }}>
                <Home size={16} /> Find a Home
              </Link>
              <Link href="/login"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                <Building2 size={16} /> List Property
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>);

}